import { Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/button'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Switch } from '@/shared/ui/switch'

const URL_REGEX = /^https?:\/\/[^\s/$.?#].\S*$/i

interface SelectOption {
  label: string
  value: string
}

export interface TourBasicInfoValue {
  duration: number
  scopeType: 'province' | 'tenantDestination'
  countryId: string
  provinceId: string
  tenantDestinationId: string
  ticketLink: string
  isFavorite: boolean
}

interface TourBasicInfoFormProps {
  value: TourBasicInfoValue
  countryOptions: SelectOption[]
  provinceOptions: SelectOption[]
  tenantDestinationOptions: SelectOption[]
  isProvinceLoading?: boolean
  isTenantDestinationLoading?: boolean
  onChange: (value: TourBasicInfoValue) => void
  onCreateDestination?: () => void
}

export function TourBasicInfoForm({
  value,
  countryOptions,
  provinceOptions,
  tenantDestinationOptions,
  isProvinceLoading,
  isTenantDestinationLoading,
  onChange,
  onCreateDestination,
}: TourBasicInfoFormProps) {
  const { t } = useTranslation()
  const [ticketLinkTouched, setTicketLinkTouched] = useState(false)

  const ticketLinkError =
    ticketLinkTouched && value.ticketLink && !URL_REGEX.test(value.ticketLink)
      ? t('tour.ticketLinkInvalid', {
          defaultValue: 'Link không hợp lệ. Phải bắt đầu bằng http:// hoặc https://',
        })
      : undefined

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField label={t('tour.duration')} required>
          <Input
            type="number"
            min={30}
            max={1440}
            value={value.duration}
            onChange={(e) => onChange({ ...value, duration: Number(e.target.value) || 0 })}
          />
        </FormField>
      </div>

      <FormField
        label={t('tour.ticketLink', { defaultValue: 'Link mua vé' })}
        error={ticketLinkError}
      >
        <Input
          type="url"
          placeholder="https://..."
          value={value.ticketLink}
          onChange={(e) => onChange({ ...value, ticketLink: e.target.value })}
          onBlur={() => setTicketLinkTouched(true)}
        />
      </FormField>

      <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50/60 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-zinc-800">
            {t('tour.isFavorite', { defaultValue: 'Tour nổi bật' })}
          </p>
          <p className="text-xs text-zinc-500">
            {t('tour.isFavoriteHint', {
              defaultValue: 'Hiển thị tour này trong danh sách nổi bật',
            })}
          </p>
        </div>
        <Switch
          checked={value.isFavorite}
          onCheckedChange={(checked) => onChange({ ...value, isFavorite: checked })}
        />
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-zinc-50/60 p-4 space-y-4">
        <p className="text-xs text-zinc-500">
          {t('tour.scopeDestinationHint', {
            defaultValue: 'Chọn điểm đến cụ thể để chỉ lấy POI thuộc khu đó.',
          })}
        </p>

        <div className="grid gap-4 lg:grid-cols-2">
          <FormField label={t('destination.country')} required>
            <Select
              value={value.countryId || '__all__'}
              onValueChange={(nextValue) => {
                const countryId = nextValue === '__all__' ? '' : nextValue
                onChange({
                  ...value,
                  countryId,
                  provinceId: '',
                  tenantDestinationId: '',
                })
              }}
            >
              <SelectTrigger className="h-11 bg-white">
                <SelectValue placeholder={t('destination.country')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">
                  {t('common.select', { defaultValue: 'Chọn' })}
                </SelectItem>
                {countryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label={t('destination.province')} required>
            <Select
              value={value.provinceId || '__all__'}
              onValueChange={(nextValue) => {
                const provinceId = nextValue === '__all__' ? '' : nextValue
                onChange({
                  ...value,
                  provinceId,
                  tenantDestinationId: '',
                })
              }}
              disabled={!value.countryId || Boolean(isProvinceLoading)}
            >
              <SelectTrigger className="h-11 bg-white">
                <SelectValue placeholder={t('destination.province')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">
                  {t('common.select', { defaultValue: 'Chọn' })}
                </SelectItem>
                {provinceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <FormField label={t('poi.destination')} required>
          <Select
            value={value.tenantDestinationId || '__all__'}
            onValueChange={(nextValue) => {
              const tenantDestinationId = nextValue === '__all__' ? '' : nextValue
              onChange({
                ...value,
                tenantDestinationId,
              })
            }}
            disabled={!value.countryId || !value.provinceId || Boolean(isTenantDestinationLoading)}
          >
            <SelectTrigger className="h-11 bg-white">
              <SelectValue placeholder={t('poi.selectDestination')} />
            </SelectTrigger>
            <SelectContent>
              {isTenantDestinationLoading ? (
                <div className="flex items-center justify-center gap-2 px-2 py-6 text-sm text-zinc-500">
                  <Loader2 size={14} className="animate-spin" />
                </div>
              ) : value.countryId && value.provinceId && tenantDestinationOptions.length === 0 ? (
                <div className="space-y-3 px-3 py-5 text-center">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-zinc-800">
                      {t('poi.destinationEmptyTitle', {
                        defaultValue: 'No tenant destinations yet',
                      })}
                    </p>
                    <p className="text-xs leading-5 text-zinc-500">
                      {t('poi.destinationEmptyDescription', {
                        defaultValue: 'Create a destination first to link it here.',
                      })}
                    </p>
                  </div>
                  {onCreateDestination && (
                    <Button type="button" size="sm" className="h-8" onClick={onCreateDestination}>
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      {t('poi.destinationCreateAction', { defaultValue: 'Create new destination' })}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <SelectItem value="__all__">
                    {t('common.select', { defaultValue: 'Chọn' })}
                  </SelectItem>
                  {tenantDestinationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          {onCreateDestination &&
            !isTenantDestinationLoading &&
            tenantDestinationOptions.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 h-9 w-full justify-start text-sm font-medium text-blue-600 hover:text-blue-700"
                onClick={onCreateDestination}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {t('poi.destinationCreateAction', { defaultValue: 'Create new destination' })}
                </span>
              </Button>
            )}
        </FormField>
      </div>
    </div>
  )
}
